import API from '@/api/base'
import {
  MFA_SESSION_BASE_PATH,
  MFA_SESSION_ENDPOINTS,
} from '@/constants/mfa-api-endpoints'
import { getTrackingKey } from '@/utils/api'

class MfaAPI extends API {
  /**
   * Initialize MFA session (Login flow).
   * POST /v1/mfa/session/init
   * @param {Object} params
   * @param {string} params.phoneNumber
   * @param {string} params.checkoutId - UUID
   * @param {string} [params.clientType] - Default CHECKOUT
   */
  initSession = ({ phoneNumber, checkoutId, clientType = 'CHECKOUT' }) => {
    return this.fetcher({
      method: 'POST',
      trackingKey: getTrackingKey(this.uri, 'init'),
      path: MFA_SESSION_ENDPOINTS.init,
      data: this.transformData({
        clientType,
        clientTypeData: {
          phoneNumber,
          checkoutId,
        },
      }),
    })
  }

  /**
   * Process the current MFA step (e.g. submit OTP or in-app decision).
   * POST /v1/mfa/session/step
   * @param {Object} params
   * @param {string} params.mfaSessionId - UUID from init response
   * @param {string} params.stepType - One of MFA_STEP_TYPES (OTP, IVR, PASSCODE, IN_APP_APPROVAL)
   * @param {Object} params.stepTypeData - e.g. { otp } for OTP, { decision: 'APPROVE'|'REJECT' } for IN_APP_APPROVAL
   */
  processStep = ({ mfaSessionId, stepType, stepTypeData }) => {
    return this.fetcher({
      method: 'POST',
      trackingKey: getTrackingKey(this.uri, 'step'),
      path: MFA_SESSION_ENDPOINTS.step,
      data: this.transformData({
        mfaSessionId,
        stepType,
        stepTypeData,
      }),
    })
  }

  /**
   * Perform an action on the current step (e.g. resend OTP).
   * POST /v1/mfa/session/step/action
   * @param {Object} params
   * @param {string} params.mfaSessionId - UUID
   * @param {string} params.stepType - One of MFA_STEP_TYPES (OTP, IVR, PASSCODE, IN_APP_APPROVAL, FACE_SCAN)
   * @param {Object} params.stepTypeData - e.g. { action: 'RESEND_OTP' | 'SEND_OTP' } for OTP, { action: 'RETRY' } for FACE_SCAN
   */
  stepAction = ({ mfaSessionId, stepType, stepTypeData }) => {
    return this.fetcher({
      method: 'POST',
      trackingKey: getTrackingKey(this.uri, 'step-action'),
      path: MFA_SESSION_ENDPOINTS.stepAction,
      data: this.transformData({
        mfaSessionId,
        stepType,
        stepTypeData,
      }),
    })
  }

  stepTransition = ({ mfaSessionId, stepType }) => {
    return this.fetcher({
      method: 'POST',
      trackingKey: getTrackingKey(this.uri, 'step-transition'),
      path: MFA_SESSION_ENDPOINTS.stepTransition,
      data: this.transformData({
        mfaSessionId,
        stepType,
      }),
    })
  }

  /**
   * Cancel an MFA session.
   * POST /auth/v1/mfa/session/cancel
   * @param {Object} params
   * @param {string} params.mfaSessionId - UUID from init response
   */
  cancelSession = ({ mfaSessionId }) => {
    return this.fetcher({
      method: 'POST',
      trackingKey: getTrackingKey(this.uri, 'cancel'),
      path: MFA_SESSION_ENDPOINTS.cancel,
      data: this.transformData({
        mfaSessionId,
      }),
    })
  }

  /**
   * Get step status for an MFA session.
   * GET /v1/mfa/session/step/{mfa_session_id}
   * @param {Object} params
   * @param {string} params.mfaSessionId - UUID
   */
  getStepStatus = ({ mfaSessionId }) => {
    return this.fetcher({
      method: 'GET',
      trackingKey: getTrackingKey(this.uri, 'step-status'),
      path: `${MFA_SESSION_BASE_PATH}/step/${mfaSessionId}`,
    })
  }
}

export default new MfaAPI(MFA_SESSION_BASE_PATH)
